import { RemovabilityConditionDto } from '../../../../../data-transfer/entities/removability-condition-dto';
import { DictionaryHandler, BooleanDictionary, TriStateDictionary, DialogActions } from '../../../../../model/dictionary';
import { FormControl, FormGroup } from '@angular/forms';
import { Input, OnInit, Output, EventEmitter, Directive } from '@angular/core';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { PackagingPartTypeDto } from 'src/app/data-transfer/entities/packaging-part-type-dto';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { CustomFieldAssignmentsDto } from 'src/app/data-transfer/entities/custom-field-assignments-dto';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { CustomFieldProfileDto } from 'src/app/data-transfer/entities/custom-field-profile-dto';
import { MatDialog } from '@angular/material/dialog';
import { CustomFieldsDialogComponent, CustomFieldsDialogResult } from 'src/app/components/dialogs/custom-fields-dialog/custom-fields-dialog.component';
import { ComponentApiService } from 'src/app/data-transfer/services/component-api-service';
import { RelevantAnalysisTypes, RelevantAnalysisService } from 'src/app/util/analyses-util/relevant-analysis-service';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { PackagingComponentDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-dto';

@Directive()
export class ComponentSubHtmlBase implements OnInit {

  @Input() form!: FormGroup;
  @Input() componentTypeSelectionLabel = '';
  @Input() componentSubtypes: PackagingPartTypeDto[] = [];
  @Input() manufacturingCountries: CountryDto[] = [];
  @Input() distributionCountries?: CountryDto[];
  @Input() canEditForm = false;
  @Input() isDecoration = false;
  @Input() isInlay = false;
  @Input() isMainBody = false;
  @Input() isTracked = false;
  @Input() isIndependentComponent = false;
  @Input() removabilityConditions?: RemovabilityConditionDto[];
  @Input()
    get relevantAnalysisTypes(): RelevantAnalysisTypes {
      return this._relevantAnalysisTypes;
    }
    set relevantAnalysisTypes(newValue: RelevantAnalysisTypes) {
      this._relevantAnalysisTypes = newValue;
      this.relevantAnalysisTypesChange.emit(newValue);
    }
  @Output() relevantAnalysisTypesChange = new EventEmitter();
  @Output() componentSubtypeChanged = new EventEmitter();
  @Output() adhesivesOptionChanged = new EventEmitter();

  private _relevantAnalysisTypes: RelevantAnalysisTypes = new RelevantAnalysisTypes(false,false,false);
  flexibilityDictionary: BooleanDictionary[];
  nirBarrierDictionary: TriStateDictionary[];
  componentRemovedDictionary: BooleanDictionary[];
  adhesivesUsedDictionary: BooleanDictionary[];
  attachedToMainBodyDictionary: BooleanDictionary[];
  hasDirectContactWithProductDictionary: TriStateDictionary[] = [];
  minimalLength = 0.001;
  showSurfaceRatioInfo = false;
  nirBarrierPlaceholder : string = '';
  packagingPartEnum = PackagingPart;

  customFieldProfiles: CustomFieldProfileDto[] = [];
  underlyingComponent: PackagingComponentDto | undefined;

  constructor(
    private dictionaryHandler: DictionaryHandler,
    private componentTypeService: ComponentTypeService,
    protected componentApiService: ComponentApiService,
    private dialog: MatDialog,
    protected relevantAnalysisService: RelevantAnalysisService,
  ) {
    this.flexibilityDictionary = this.dictionaryHandler.getFlexibilityDictionary();
    this.componentRemovedDictionary = this.dictionaryHandler.getYesNoDictionary();
    this.adhesivesUsedDictionary = this.dictionaryHandler.getYesNoDictionary();
    this.attachedToMainBodyDictionary = this.dictionaryHandler.getYesNoDictionary();
    this.nirBarrierDictionary = this.dictionaryHandler.getNirBarrierDictionary();
    this.hasDirectContactWithProductDictionary = this.dictionaryHandler.hasDirectContactWithProductDictionary();
  }

  ngOnInit(): void {
    this.reloadCustomFieldProfiles();

    if (!this.isIndependentComponent && !this.isInlay && !this.isMainBody) {
      this.setRemovedByConsumerValueAndValidators(this.form.controls.removedBeforeUsage.value);
      this.setSeperationValueAndValidators(this.form.controls.removedBeforeUsage.value);
    }
    this.addRemoveAdhesionFields();
    this.form.controls.packagingComponentSubtypeId.valueChanges.subscribe(newId => {
      this.reloadCustomFieldProfiles();
      this.form.controls.packagingComponentSubtypeName.patchValue(
        this.componentSubtypes.find(x => x.id === newId)?.name ?? '');
    });
  }

  onComponentTypeChange(subtypeId: number) {
    if (!this.isDecoration) { return; }
    this.addRemoveAdhesionFields();
    this.componentSubtypeChanged.emit(subtypeId);
  }

  onAdhesivesOptionChange(event: boolean) {
    this.adhesivesOptionChanged.emit(event);
  }

  onRemovedBeforeUsageChange(newRemovedBeforeUsage: boolean) {
    this.setRemovedByConsumerValueAndValidators(newRemovedBeforeUsage);
    this.setSeperationValueAndValidators(newRemovedBeforeUsage);
  }

  showCustomFields() {
    let internallyTrackedAssignments: CustomFieldAssignmentsDto | undefined;

    if (this.form.controls.hasInternalTracking?.value) {
      internallyTrackedAssignments = this.underlyingComponent?.customFields ?? new CustomFieldAssignmentsDto();
    }

    const dialogConfig = getDialogConfig({ profiles: this.customFieldProfiles,
                                           assignments: this.form.controls.customFields.value ?? new CustomFieldAssignmentsDto(),
                                           canEditForm: this.canEditForm,
                                           internallyTrackedAssignments: internallyTrackedAssignments }, '1500px');

    const dialogRef = this.dialog.open<CustomFieldsDialogComponent, any, CustomFieldsDialogResult>
                                    (CustomFieldsDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result?.event == DialogActions.CONFIRM) {
        this.form.controls.customFields.setValue(result.assignments);
        if(result.isCustomFieldChanged){
          this.form.markAsDirty();
        }
      }
    });
  }

  private reloadCustomFieldProfiles() {
    const componentSubtypeId = this.form.controls.packagingComponentSubtypeId.value;

    if (!componentSubtypeId) {
      this.customFieldProfiles = [];
      return;
    }
    this.componentApiService.getCustomFieldProfiles(componentSubtypeId).subscribe(profiles => {
      this.customFieldProfiles = profiles;
    });
  }

  private setRemovedByConsumerValueAndValidators(removedBeforeUsage: boolean) {
    if (!this.canEditForm || this.isTracked) { return; }
    const removedByConsumerControl = this.form.controls.removedByConsumer;
    if (removedBeforeUsage) {
      removedByConsumerControl.disable();
      removedByConsumerControl.clearValidators();
      removedByConsumerControl.patchValue(true);

    } else {
      removedByConsumerControl.enable();
    }
  }

  private setSeperationValueAndValidators(removedBeforeUsage: boolean) {
    if (!this.canEditForm || this.isTracked) { return; }
    const seperationControl = this.form.controls.separationThroughCollectionAndSorting;
    if (removedBeforeUsage) {
      seperationControl.disable();
      seperationControl.clearValidators();
      seperationControl.patchValue(true);

    } else {
      seperationControl.enable();
    }
  }

  private addRemoveAdhesionFields() {
    if (!this.isDecoration) { return; }
    if (!this.isIndependentComponent &&
      (this.componentTypeService.isDecorationOfTypeLabel(this.form.getRawValue().packagingComponentSubtypeId) ||
      this.componentTypeService.isDecorationOfTypeInMold(this.form.getRawValue().packagingComponentSubtypeId))) {
      if (!this.form.controls.isAdhesiveUsed) {
        this.form.addControl('isAdhesiveUsed', new FormControl(null, []));
      }
      if (!this.form.controls.removabilityConditionId) {
        this.form.addControl('removabilityConditionId', new FormControl(null, []));
      }
    } else {
      if (this.form.controls.isAdhesiveUsed) {
        this.form.removeControl('isAdhesiveUsed');
      }
      if (this.form.controls.removabilityConditionId) {
        this.form.removeControl('removabilityConditionId');
      }
    }
  }
}
